import React from "react";

import { Group } from "@/shared/components/settings-atoms";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { initialsOf, personName } from "./copy";

import type { RunInterface } from "@/shared/types";
import type { RuleMatches } from "../../types";

/* The people who've written to the agent and land on this policy: up to six, then how many
   more. A policy that's off takes nobody, and says how many it would. `shadow` is the line
   about the people it matches who meet an earlier policy first. */
export function WhoLands({ matches, off, agentName, shadow }: {
  matches: RuleMatches;
  off: boolean;
  agentName: string;
  shadow: string | null;
}) {
  const shown = off ? [] : matches.sample;
  const count = off ? 0 : matches.count;
  return (
    <Group
      title={<>Who lands on it <span className="font-mono tabular-nums text-[12px] font-normal text-[var(--text-quaternary)]">{count}</span></>}
      desc={`People who've written to ${agentName} and meet this policy first.`}
    >
      <div aria-label="Who lands on it" className="rounded-[12px] ring-1 ring-[var(--hairline)] px-3 divide-y divide-[var(--hairline)]">
        {shown.map((p) => {
          const name = personName(p);
          return (
            <div key={p.id} className="flex items-center gap-3 py-2.5 min-w-0">
              <span className="w-6 h-6 shrink-0 rounded-[7px] bg-[var(--surface-2)] flex items-center justify-center text-[10.5px] font-semibold text-[var(--text-secondary)]">
                {initialsOf(name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-[var(--text-primary)]">{name}</span>
                <span className="block mt-px truncate text-[12px] text-[var(--text-tertiary)]">{p.email ?? "No email yet"}</span>
              </span>
              <span className="flex gap-1 shrink-0">
                {p.channels.map((c) => <ViaIcon key={c} type={c as RunInterface} />)}
              </span>
            </div>
          );
        })}
        {!shown.length && (
          <div className="py-3.5 text-[12.5px] text-[var(--text-tertiary)]">
            {off
              ? `Nobody — it's off. Switch it on in the header.${matches.count ? ` ${matches.count} would land on it.` : ""}`
              : "Nobody right now."}
          </div>
        )}
        {count > shown.length && (
          <div className="py-2.5 text-[12px] text-[var(--text-tertiary)]">and <span className="font-mono tabular-nums">{count - shown.length}</span> more</div>
        )}
      </div>
      {shadow && <p className="m-0 mt-3 text-[12px] leading-[1.55] text-[var(--text-quaternary)]">{shadow}</p>}
    </Group>
  );
}
